import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import Card from '../components/Card';
import Navbar from '../components/Navbar';
import VideoViewer from '../components/VideoViewer';
import { theme } from '../utils/theme';
import i18n from '../i18n';
import PhotoGuideStep from './PhotoGuideStep';

// Helper function to get localized value from a multilingual field object
const getLocalizedValue = (obj, lang = i18n.language) => {
  if (!obj) return '';
  if (typeof obj !== 'object') return obj;
  if (obj[lang]) return obj[lang];
  
  // Fallback to first available language
  const firstLang = Object.keys(obj)[0];
  return obj[firstLang] || '';
};

// Helper to generate a unique ID
const generateUniqueId = () => `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [buckets, setBuckets] = useState([]);
  const [processingContents, setProcessingContents] = useState(new Set());
  const [doneContents, setDoneContents] = useState(new Set());
  const [contentStatuses, setContentStatuses] = useState({});
  const [pollInterval, setPollInterval] = useState(null);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showDashboard, setShowDashboard] = useState(true);
  const [showingPhotoGuide, setShowingPhotoGuide] = useState(false);
  const [activeBucketId, setActiveBucketId] = useState(null);
  const bucketRefs = useRef({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 5;

  // Test item for "processing" state
  const [showTestItem, setShowTestItem] = useState(true);
  // Using a secure signed URL from the server instead of a local file path
  const testImageUrl = "https://thememorymotion.com/api/files/access?path=1743613763676-17593872.jpeg&type=upload&expires=1743778498&signature=076a49cef530dac35d6ee0d9c213d72de60bb463b1f7db59dcb20ed3959b2b29";

  const getDaysRemaining = (bucket) => {
    var deletionDate = new Date();
    deletionDate.setDate(deletionDate.getDate() + 30)
    if(bucket.deletionDate!=null){
      deletionDate = new Date(bucket.deletionDate);
    }
    else{
      deletionDate = new Date(bucket.updatedAt);
      deletionDate.setDate(deletionDate.getDate() + 30)
    }
    const today = new Date();
    return Math.round(Math.abs((deletionDate - today) / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    fetchContent();
    // Cleanup on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, []);

  // Handle polling for processing contents
  useEffect(() => {
    // Clean up previous interval if it exists
    if (pollInterval) {
      clearInterval(pollInterval);
    }

    // Only start polling if there are processing contents
    if (processingContents.size > 0) {
      // Then set up polling every 10 seconds
      const interval = setInterval(checkProcessingStatus, 10000);
      setPollInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [processingContents]); 

  // Perform initial check for both processing and done contents
  useEffect(() => {
    if (!initialCheckDone && (processingContents.size > 0 || doneContents.size > 0)) {
      // Wait a moment before first check to avoid race conditions
      const initialCheckTimeout = setTimeout(() => {
        checkInitialBatchStatus();
      }, 2000);

      return () => clearTimeout(initialCheckTimeout);
    }
  }, [initialCheckDone, processingContents, doneContents]);

  const fetchContent = async () => {
    try {
      const response = await api.get(`/api/content/my?page=${currentPage}&limit=${itemsPerPage}`);
      // Filter out archived buckets before setting state
      const activeBuckets = (response.data.buckets || []).filter(bucket => bucket.status !== 'archived');
      setBuckets(activeBuckets);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.total);
      
      // Collect processing content IDs and initialize statuses
      const processing = new Set();
      const initialStatuses = {};
      
      activeBuckets.forEach(bucket => {
        bucket.Contents.forEach(content => {
          // Initialize status for each content using processed videos if available
          initialStatuses[content.id] = {
            status: content.status,
            processedVideos: content.ProcessedContents || []
          };
          
          if (content.status === 'processing') {
            processing.add(content.id);
          }
        });
      });

      setProcessingContents(processing);
      setContentStatuses(initialStatuses);
      
      // Only set initialCheckDone to false if there are processing items
      setInitialCheckDone(processing.size === 0);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  // Initial check for both processing and done contents
  const checkInitialBatchStatus = async () => {
    const idsToCheck = [...processingContents, ...doneContents];
    
    if (idsToCheck.length === 0) {
      setInitialCheckDone(true);
      return;
    }

    try {
      console.log("Making initial batch status check for", idsToCheck.length, "items");
      const response = await api.post('/api/content/batch-status', {
        contentIds: idsToCheck
      });

      const newStatuses = response.data.statuses;
      
      // Process the statuses
      const stillProcessing = new Set();

      for (const [id, status] of Object.entries(newStatuses)) {
        if (status.status === 'processing') {
          stillProcessing.add(id);
        }
        // We don't need to track 'done' items after initial fetch
      }

      // Update statuses with the processed videos info
      setContentStatuses(prev => ({ ...prev, ...newStatuses }));
      setProcessingContents(stillProcessing);
      setInitialCheckDone(true); // Mark initial check as done
    } catch (error) {
      console.error('Error checking initial batch status:', error);
      setInitialCheckDone(true); // Mark as done even on error
    }
  };

  // Regular polling function for processing items only
  const checkProcessingStatus = async () => {
    if (processingContents.size === 0) {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
      return;
    }

    try {
      console.log("Polling for", processingContents.size, "processing items");
      const response = await api.post('/api/content/batch-status', {
        contentIds: Array.from(processingContents)
      });

      const newStatuses = response.data.statuses;
      
      // Only keep items that are still processing
      const stillProcessing = new Set();

      for (const [id, status] of Object.entries(newStatuses)) {
        if (status.status === 'processing') {
          stillProcessing.add(id);
        }
      }

      // Update statuses
      setContentStatuses(prev => ({ ...prev, ...newStatuses }));
      
      // Update processing set
      setProcessingContents(stillProcessing);

      // If nothing is processing anymore, clear the interval
      if (stillProcessing.size === 0 && pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    } catch (error) {
      console.error('Error checking processing status:', error);
    }
  };

  const handleDelete = async (contentId, bucketId) => {
    try {
      // First, get the content details to get the filename
      const contentResponse = await api.get(`/api/content/${contentId}`);
      const content = contentResponse.data.content;
      
      // Extract filename from the photoUrl
      const filename = content.photoUrl.split('/uploads/').pop();
      
      // Delete the file and content record
      await api.delete(`/api/content/file/${filename}`);
      
      // Refresh content after deletion - the backend will handle bucket deletion if it's empty
      fetchContent();
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const handlePayNow = async (bucketId) => {
    try {
      // Get bucket content and store in session storage
      const response = await api.get(`/api/buckets/${bucketId}`);
      console.log('Bucket response:', response.data);

      if (!response.data.bucket || !response.data.bucket.Contents) {
        throw new Error('Invalid bucket data received');
      }

      const bucket = response.data.bucket;
      
      // Map each content item to the expected uploads format
      const uploads = bucket.Contents.map(content => ({
        id: generateUniqueId(),
        file: null, // Can't restore actual file object
        preview: content.photoUrl, // Keep the full URL for display
        imageTheme: getLocalizedValue(content.Prompt?.imageTheme), // Get localized theme
        actions: [{
          promptId: content.PromptId,
          variety: content.variety || 1
        }],
        filename: content.photoUrl.split('/uploads/').pop(), // Extract filename for display
        contentId: content.id,
        status: content.status,
        createdAt: content.createdAt
      }));

      console.log('Storing uploads in session:', uploads);
      sessionStorage.setItem('existingBucketId', bucketId);
      sessionStorage.setItem('uploads', JSON.stringify(uploads));
      
      // Navigate to the process page at step 1 (upload step)
      navigate('/process');
    } catch (error) {
      console.error('Error initiating payment:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleDeleteBucket = async (bucketId) => {
    if (window.confirm("Are you sure you want to delete this upload? This will delete all images and their generated content.")) {
      try {
        await api.delete(`/api/buckets/${bucketId}`);
        // Refresh the dashboard after deletion
        fetchContent();
      } catch (error) {
        console.error('Error deleting bucket:', error);
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    // Use ISO format for consistency across all locales
    return new Date(dateString).toISOString().split('T')[0];
  };

  const getBucketTitle = (bucket) => {
    if (bucket.status === 'pending') {
      return t('cartSection.title');
    }
    return t('purchaseInfo', { date: formatDate(bucket.createdAt) });
  };

  const handleStartNewUpload = () => {
    // Clear any existing bucket ID from session storage
    sessionStorage.removeItem('existingBucketId');
    sessionStorage.removeItem('uploads');
    navigate('/process');
  };

  const handleVideoClick = (video, bucketId) => {
    setSelectedVideo(video);
    setActiveBucketId(bucketId);
    setShowDashboard(false);
  };

  const handleCloseVideo = (bucketId) => {
    setSelectedVideo(null);
    setShowDashboard(true);
    
    // Wait for the dashboard to render before scrolling
    setTimeout(() => {
      if (bucketId && bucketRefs.current[bucketId]) {
        bucketRefs.current[bucketId].scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 0);
  };

  useEffect(() => {
    fetchContent();
  }, [currentPage]); // Re-fetch when page changes

  const handlePageChange = (newPage) => {
    setSelectedVideo(null);
    setShowDashboard(true);
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center space-x-2 my-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} ${
            currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {t('pagination.previous')}
        </button>
        <span className="text-sm text-gray-600">
          {t('pagination.pageInfo', { current: currentPage, total: totalPages })}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} ${
            currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {t('pagination.next')}
        </button>
      </div>
    );
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <h2 className={`${theme.text.h2} text-center mb-8`}>{t('emptyState.title')}</h2>
      <div className="grid md:grid-cols-2 gap-6 w-full max-w-3xl">
        <button
          onClick={handleStartNewUpload}
          className="flex flex-col items-center p-8 bg-[#FFBA00] hover:bg-[#FFBA00]/90 text-white rounded-xl border-2 border-[#FFBA00] hover:border-[#FFBA00]/70 hover:shadow-lg transition-all duration-200"
        >
          <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-white/20">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <h3 className={`${theme.text.h3} text-center mb-2 text-white`}>{t('emptyState.uploadTitle')}</h3>
          <p className="text-center text-white/90">{t('emptyState.uploadDescription')}</p>
        </button>

        <button
          onClick={() => setShowingPhotoGuide(true)}
          className="flex flex-col items-center p-8 bg-white rounded-xl border-2 border-[#6D9773] hover:border-[#0C3B2E] hover:shadow-lg transition-all duration-200"
        >
          <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-[#6D9773]/10">
            <svg className="w-8 h-8 text-[#0C3B2E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className={`${theme.text.h3} text-center mb-2`}>{t('emptyState.guideTitle')}</h3>
          <p className="text-center text-gray-600">{t('emptyState.guideDescription')}</p>
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <Navbar />
      {showingPhotoGuide ? (
        <div className="container mx-auto p-4 mt-16">
          <PhotoGuideStep onBack={() => setShowingPhotoGuide(false)} />
        </div>
      ) : showDashboard ? (
        <div className="container mx-auto p-4 mt-16">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
              {totalItems > 0 && (
                <span className="text-sm text-gray-500">
                  {t('totalSets', { count: totalItems })}
                </span>
              )}
            </div>
            <button
              onClick={handleStartNewUpload}
              className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.md} flex items-center`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              {t('addMemory')}
            </button>
          </div>
          
          {buckets.length === 0 ? <EmptyState /> : (
            <>
              {totalPages > 1 && <PaginationControls />}
              <div className="space-y-8">
                {buckets.map((bucket) => (
                  <div 
                    key={bucket.id}
                    ref={el => bucketRefs.current[bucket.id] = el}
                    className={`bg-gradient-to-b from-white to-gray-50 rounded-xl shadow-lg overflow-hidden border border-gray-200 transition-all duration-200 ${bucket.status === 'pending' ? 'ring-2 ring-yellow-200' : ''}`}
                  >
                    <div className={`border-b ${bucket.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-gray-200'}`}>
                      <div className="px-6 py-4">
                        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                          <div className="w-full">
                            <div className="flex flex-col md:flex-row md:items-center justify-between">
                              <h2 className={`${theme.text.h3} mb-2 md:mb-0`}>
                                {getBucketTitle(bucket)}
                              </h2>
                              <div className="flex flex-col md:hidden gap-2 order-last mt-3 min-w-[200px]">
                                {bucket.status === 'pending' && (
                                  <button
                                    onClick={() => handlePayNow(bucket.id)}
                                    disabled={!bucket.Contents?.length}
                                    className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.md} w-full ${!bucket.Contents?.length ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  >
                                    {t('cartSection.payNow')}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteBucket(bucket.id)}
                                  className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} w-full flex items-center justify-center`}
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  {t('cartSection.deleteSet')}
                                </button>
                              </div>
                            </div>
                            {bucket.status === 'pending' && bucket.Contents?.length > 0 && (
                              <div className="mt-2">
                                <div className="inline-flex items-center px-3 py-1 rounded-lg space-x-2 bg-yellow-100 text-yellow-700">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  <span className="text-sm font-medium">
                                    {t('cartSection.paymentRequired', { count: bucket.Contents.length })}
                                  </span>
                                </div>
                              </div>
                            )}
                            {bucket.status === 'pending' && bucket.createdAt && (
                              <div className="mt-2">
                                <div className={`inline-flex items-center px-3 py-1 rounded-lg space-x-2 
                                  ${getDaysRemaining(bucket) <= 5 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-sm font-medium">
                                    {t('cartSection.filesDeleteWarning', { days: getDaysRemaining(bucket) })}
                                  </span>
                                </div>
                              </div>
                            )}
                            {bucket.status === 'paid' && bucket.createdAt && (
                              <div className="mt-2">
                                <div className={`inline-flex items-center px-3 py-1 rounded-lg space-x-2 
                                  ${getDaysRemaining(bucket) <= 5 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-sm font-medium">
                                    {t('cartSection.filesDeleteWarning', { days: getDaysRemaining(bucket) })}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="hidden md:flex flex-col gap-2 min-w-[200px]">
                            {bucket.status === 'pending' && (
                              <button
                                onClick={() => handlePayNow(bucket.id)}
                                disabled={!bucket.Contents?.length}
                                className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.md} ${!bucket.Contents?.length ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {t('cartSection.payNow')}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteBucket(bucket.id)}
                              className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} w-full flex items-center justify-center`}
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              {t('cartSection.deleteSet')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      {bucket.Contents.map((content) => (
                        <Card
                          key={content.id}
                          id={content.id}
                          image={content.photoUrl}  
                          prompt={content.Prompt ? getLocalizedValue(content.Prompt.prompt) : ''}
                          action={content.Prompt ? getLocalizedValue(content.Prompt.action) : ''}
                          variety={content.variety || 1}
                          status={contentStatuses[content.id]?.status}
                          processedVideos={contentStatuses[content.id]?.processedVideos}
                          onDelete={() => handleDelete(content.id, bucket.id)}
                          onVideoClick={(video) => handleVideoClick(video, bucket.id)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {totalPages > 1 && <PaginationControls />}
            </>
          )}
        </div>
      ) : selectedVideo && (
        <div className="container mx-auto p-4 mt-16">
          <VideoViewer 
            video={selectedVideo} 
            onBack={handleCloseVideo} 
            bucketId={activeBucketId}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;